import React from 'react';
import { MarginToolCheckbox } from '@AMIEWEB/MarginTool/Common/MarginToolCheckbox';
import { Divider, Grid, Typography, useTheme } from '@mui/material';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { formatDate } from '@AMIEWEB/MarginTool/helper';
import { MarginToolCardTitle } from '../../../Common/MarginToolCardTitle';
import { DatesAndDuration } from './DatesAndDuration';
import MarginToolTextField from '@AMIEWEB/MarginTool/Common/MarginToolTextField';

import {
  selectAddPlacementMarginExtention,
  selectAddPlacementMarginScenario,
  selectPlacementMarginBookingPeriod,
  selectCurrentEditPlacementMarginScenarioDetails,
  selectPlacementMarginScenarioModalState,
  selectSelectedPlacementMarginScenario,
} from 'store/redux-store/margin-tool/slices/placement/placement-add-edit-scenario/add-edit-placement-scenario.selector';
import { PackageType, PayPackageOptions } from '@AMIEWEB/MarginTool/enum';
import { useAddScenarioStyles } from './AddScenario.styles';
import { disableExtension, disableExtensionOfPriorPlacementID } from './helper';
import { useIsAssignmentScenario } from '@AMIEWEB/MarginTool/hooks';
import { missingField } from 'app/constants';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

const BookingData = () => {
  const { classes } = useAddScenarioStyles();
  const theme = useTheme();
  const { setValue, watch, formState: { errors }, clearErrors } = useFormContext();
  const { t } = useTranslation();
  const addScenario = useSelector(selectAddPlacementMarginScenario);
  const addExtension = useSelector(selectAddPlacementMarginExtention);
  const selectedScenario = useSelector(selectSelectedPlacementMarginScenario);
  const selectBookingPeriodDetails = useSelector(selectPlacementMarginBookingPeriod);
  const editScenarioFormDetails = useSelector(selectCurrentEditPlacementMarginScenarioDetails);
  const extensionValue = watch('isInExtension');
  const isModalOpen = useSelector(selectPlacementMarginScenarioModalState);
  const isAssignmentScenario = selectBookingPeriodDetails?.packageName === PackageType.Assignment; // comparing with packagename , will update once flag is introduced
  const isExtensionDisabled = disableExtension(isModalOpen?.modalName, isAssignmentScenario, addScenario);
  const isExtensionOfPriorPlacementIDDisabled = disableExtensionOfPriorPlacementID(
    isModalOpen?.modalName,
    isAssignmentScenario,
    extensionValue,
    addScenario?.isExtensionFromPriorPlacementId,
  );
  const defaultExtensionOfPriorPlacementIDValue = watch('extensionOfPriorPlacementID');
  const { isFromAssignmentBookingPeriod } = useIsAssignmentScenario();

  const handleChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    let inputValue = event.target.value.replace(/[^0-9]/g, '');
    if (inputValue.length > 16) {
      inputValue = inputValue.slice(0, 16);
    }
    setValue('extensionOfPriorPlacementID', inputValue, {
      shouldValidate: true
      });
  };

  const payCycleShiftType =
    isModalOpen?.modalName === PayPackageOptions.AddScenario
      ? addScenario?.payCycleShiftType
      : isModalOpen?.modalName === PayPackageOptions.editScenario
      ? editScenarioFormDetails?.payCycleShiftType
      : addExtension?.payCycleShiftType;

  const splitsList = useWatch({ name: 'splitsList' });


  const getPayCycleDays = (): { startDay: string | null; endDay: string | null } => {
    if (payCycleShiftType === 'Separate') {
      const firstShiftTypeId = splitsList?.[0]?.shift?.object?.shiftTypeId;
      if (firstShiftTypeId != null) {
        const shiftDetails =
          isModalOpen?.modalName === PayPackageOptions.AddScenario
            ? addScenario?.payCycleShiftDetails
            : isModalOpen?.modalName === PayPackageOptions.editScenario
            ? editScenarioFormDetails?.payCycleShiftDetails
            : addExtension?.payCycleShiftDetails;
        const match = shiftDetails?.find(d => d.shiftTypeId === firstShiftTypeId);
        if (match) {
          return { startDay: match.startDayName, endDay: match.endDayName };
        }
      }
    }
    const isAddScenario = isModalOpen?.modalName === PayPackageOptions.AddScenario;
    const startDay = isAddScenario ? addScenario?.payCycleStartDay : selectBookingPeriodDetails?.payCycleStartDay;
    const endDay = isAddScenario ? addScenario?.payCycleEndDay : selectBookingPeriodDetails?.payCycleEndDay;
    return { startDay, endDay };
  };

  const title =
    isModalOpen?.modalName === PayPackageOptions.AddScenario
      ? addScenario?.placementId > 0
        ? addScenario?.placementId
        : missingField
      : isModalOpen?.modalName === PayPackageOptions.editScenario
      ? selectedScenario?.placementId
      : addExtension?.placementId > 0
      ? addExtension?.placementId
      : missingField;

  const formatDurationAndDates = (startDate, endDate, duration) =>
    startDate && endDate && duration ? (
      <>
        <span style={{ color: theme.palette.framework.system.neutralGray, paddingLeft: '10px', paddingRight: '10px' }}>
          {'|'}
        </span>
        <span>{`${formatDate(startDate)} - ${formatDate(endDate)} (${duration} weeks)`}</span>
      </>
    ) : (
      ''
    );

  const formatPayCycle = (payCycleStartDay: string, payCycleEndDay: string, shiftType?: string) => {
    const typeLabel = shiftType ? ` (${shiftType})` : '';
    return payCycleStartDay && payCycleEndDay ? (
      <>
        <span style={{ color: theme.palette.framework.system.neutralGray, paddingLeft: '10px', paddingRight: '10px' }}>
          {'|'}
        </span>
        <span>{`${t('marginTool.labels.payCycle')}${typeLabel}: ${payCycleStartDay} - ${payCycleEndDay}`}</span>
      </>
    ) : (
      <>
        <span style={{ color: theme.palette.framework.system.neutralGray, paddingLeft: '10px', paddingRight: '10px' }}>
          {'|'}
        </span>
        <span>{`${t('marginTool.labels.payCycle')}${typeLabel}: ${missingField}`}</span>
      </>
    );
  };

  const customSubtitle = (
    <>
      {isModalOpen?.modalName === PayPackageOptions.AddScenario && (
        <>
          {formatDurationAndDates(
            addScenario?.placementStartDate,
            addScenario?.placementEndDate,
            addScenario?.placementDuration,
          )}
          {(() => {
            const { startDay, endDay } = getPayCycleDays();
            return formatPayCycle(startDay, endDay, payCycleShiftType);
          })()}
        </>
      )}

      {isModalOpen?.modalName === PayPackageOptions.editScenario && (
        <>
          {formatDurationAndDates(
            editScenarioFormDetails?.startDate,
            editScenarioFormDetails?.endDate,
            editScenarioFormDetails?.duration,
          )}
          {(() => {
            const { startDay, endDay } = getPayCycleDays();
            return formatPayCycle(startDay, endDay, payCycleShiftType);
          })()}
        </>
      )}

      {isModalOpen?.modalName === PayPackageOptions.AddExtension && (
        <>
          {formatDurationAndDates(
            addExtension?.placementStartDate,
            addExtension?.placementEndDate,
            addExtension?.placementDuration,
          )}
          {(() => {
            const { startDay, endDay } = getPayCycleDays();
            return formatPayCycle(startDay, endDay, payCycleShiftType);
          })()}
        </>
      )}
    </>
  );

  const handleExtensionChange = (extValue: boolean) => {
    if (!extValue) {
      setValue('extensionOfPriorPlacementID', '');
      clearErrors('extensionOfPriorPlacementID');
    }
  };

  return (
    <Grid container direction="column">
      <Grid item sx={{ mb: '12px' }}>
        <MarginToolCardTitle title={`PID ${title}`} customSubtitle={customSubtitle} />
      </Grid>
      <Grid container item>
        <DatesAndDuration />
      </Grid>
      <Divider className={classes.divider} />
      <Grid container item sx={{ paddingLeft: '11px' }} direction="row">
        <Grid item>
          <MarginToolCheckbox
            name="isInExtension"
            id="marginTool-addScenarioModal-isInExtensionCheckbox"
            defaultChecked={extensionValue}
            disabled={isExtensionDisabled || !isFromAssignmentBookingPeriod}
            label={
              isModalOpen?.modalName === PayPackageOptions.AddScenario ||
              (isModalOpen?.modalName === PayPackageOptions.editScenario && isAssignmentScenario)
                ? t('marginTool.labels.placementChange')
                : t('marginTool.labels.extensionFrom')
            }
            onHandleChange={(value: boolean) => handleExtensionChange(value)}
          />
        </Grid>
        <Grid item sx={{ width: '180px', marginLeft: '42px' }}>
          <MarginToolTextField
            onHandleChange={(event: React.ChangeEvent<HTMLInputElement>) => handleChange(event)}
            name="extensionOfPriorPlacementID"
            id="marginTool-addScenarioModal-placementIdTextfield"
            label={`${t('marginTool.labels.pidWithAsterisk')} ${extensionValue ? '*' : ''}`}
            rules={{ required: extensionValue ? 'Required' : false }}
            disabled={isExtensionOfPriorPlacementIDDisabled || !isFromAssignmentBookingPeriod}
            error={!!errors?.extensionOfPriorPlacementID}
            helperText={errors?.extensionOfPriorPlacementID ? errors?.extensionOfPriorPlacementID?.message : undefined}
            shrinkLabel={!!defaultExtensionOfPriorPlacementIDValue}
            defaultValue={extensionValue ? defaultExtensionOfPriorPlacementIDValue : ''}
          />
        </Grid>
      </Grid>
      {isModalOpen?.modalName === PayPackageOptions.editScenario && (
        <Grid item direction="row">
          <div style={{ display: 'flex', alignItems: 'flex-start', marginTop: '10px' }}>
            <InfoOutlinedIcon style={{ fontSize: 20, marginRight: 4, marginTop: '-2px', color: '#75787b' }} />
            <Typography variant="subtitle2" style={{ color: '#75787b', fontWeight: 400, fontSize: '12px' }}>
              {t('marginTool.labels.placementChangeSubLbl')}
            </Typography>
          </div>
        </Grid>
      )}
    </Grid>
  );
};

export default BookingData;
